import Page from '@/components/Page';
import { useForm } from '@mantine/form';
import { Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from '@/routes';
import { IconArrowNarrowLeft } from '@tabler/icons-react';
import { getMutationResult } from '@/helpers/query';
import { useGetPaymentLinkMutation } from '@/services/api';
import { updateUser } from '@/data/features/user/user';
import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import Signature from '@/components/Signature';
import classes from './style.module.css';

const Agree = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [apiGetPaymentLink] = useGetPaymentLinkMutation();
  const [allowSignature, setAllowSignature] = useState(false); //  Changed to false initially
  const navigate = useNavigate();
  const offerStore = useSelector((state) => state.offer);
  const { routeType, id } = offerStore;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const user = useSelector((state) => state.user); // Get user state from Redux

  const form = useForm({
    initialValues: {
      userSignature: user.userSignature || '', // Initialize from Redux
    },
    validate: {
      userSignature: (value) => (
        !value && allowSignature ? 'Signature is required' : null
      ),
    },
  });
  console.log({ user });

  const handleContinue = async () => {
    setLoading(true);
    try {
      if (!form.values.userSignature) {
        form.setFieldError('userSignature', 'Signature is required');
        return;
      }
      const paymentData = {
        returnValue: id,
        amount: routeType.totalPrice,
        document: {
          to: user.fullName,
          email: user.email,
          products: [
            {
              description: routeType.hebrew,
              unitCost: routeType.totalPrice,
            },
          ],
        },
      };

      const { data } = await getMutationResult(paymentData, apiGetPaymentLink);

      dispatch(updateUser({ userSignature: form.values.userSignature, linkToPay: data.data.Url }));
      // navigate(data.data.Url);
      window.location.href = data.data.Url; // Redirect to the payment link
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (offerStore.loan.title === 'mortgageRefinancing') {
      navigate(routes.mortgageCycleSummary.path);
    } else {
      navigate(routes.summary.path);
    }
  };

  const actions = (
    <>
      <Button onClick={handleBack} variant="transparent">{t('globals.back')}</Button>
      <Button
        onClick={handleContinue}
        rightSection={<IconArrowNarrowLeft size={16} />}
        disabled={!form.values.userSignature}
        loading={loading}
      >
        {t('globals.continue')}
      </Button>
    </>
  );

  const side = <div></div>;

  const agreeBoxRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      const el = agreeBoxRef.current;
      if (!el) return;

      // Check if user has scrolled to bottom (with 20px tolerance)
      const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 20;

      if (isAtBottom) {
        setAllowSignature(true);
        el.removeEventListener('scroll', handleScroll); // Remove listener after enabling
      }
    };

    const el = agreeBoxRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);

      // Also check if content is already visible (no scrolling needed)
      if (form.values.userSignature || el.scrollHeight <= el.clientHeight) {
        setAllowSignature(true);
      }
    }

    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <Page
      side={side}
      actions={actions}
      title={t('pages.agree.title')}
      subTitle={t('pages.agree.subTitle')}
      sideSize="sm"
    >
      <div ref={agreeBoxRef} className={classes.agreeBox}>
        <ol>
          <li>
            <strong>מהות השירות</strong>
            <p>
              שירות הייעוץ ההיברידי המקוצר ("השירות") כולל הנחיות לקבלת הלוואה כנגד רישום
              משכנתא, והמלצה מותאמת אישית לתמהיל משכנתא, על בסיס נתונים שהוזנו על ידי הלקוח
              במערכת הייעוץ הדיגיטלית של החברה ("המערכת"). השירות כולל:
            </p>
            <ul>
              <li>שימוש במערכת להזנת נתונים אישיים, פיננסיים ופרטי העסקה</li>
              <li>הפקת דוח המלצות מפורט הכולל הנחיות להמשך פעולה עצמאית מול הגורמים המממנים</li>
              <li>הצעת תמהיל הלוואה מותאם אישית, הכולל תנאים מומלצים במסלולים השונים</li>
              <li>פגישת ייעוץ אישית אחת (בשיחת וידאו או טלפון), עם יועץ מטעם החברה</li>
              <li>
                ידוע ללקוח כי השירות הניתן על-ידי החברה וההמלצות שיוצגו בפניו במסגרת הדוח
                שיופק במערכת, נועדו לסייע בידו לצרכי ניהול משא ומתן מול הגורם המממן והם אינם
                מחייבים את הגורם המממן
              </li>
            </ul>
          </li>

          <li>
            <strong>תהליך השירות</strong>
            <ul>
              <li>הלקוח ימלא שאלון דיגיטלי הכולל
                את כל המידע והמסמכים הנדרשים לבחינת זכאותו להלוואה</li>
              <li>בתום מילוי הנתונים, יופק דוח הנחיות הכולל תמהיל הלוואה מותאם אישית</li>
              <li>
                לאחר הפקת הדוח, תינתן ללקוח פגישת ייעוץ של 45 דקות עם יועץ משכנתאות לאימות הנתונים,
                מתן הסברים ומענה לשאלות
              </li>
            </ul>
          </li>

          <li>
            <strong>התמורה</strong>
            <p>עלות השירות הינה 1,652 ש"ח כולל מע"מ,
              והיא תשולם לחברה בכרטיס אשראי באמצעות ממשק סליקה במערכת.</p>
          </li>

          <li>
            <strong>כללי</strong>
            <ol type="1">
              <li>הדוח וההמלצות מותאמים אישית ללקוח
                ואינם ניתנים להעברה, למעט לצורך מו"מ מול גורמים מממנים</li>
              <li>ההמלצות מבוססות על נתוני הלקוח כפי
                שהוזנו, ובהתאם לתנאי השוק במועד הייעוץ, לרבות מקרים דומים</li>
              <li>הלקוח מתחייב למסור מידע מלא ומדויק, ומובהר כי החברה לא תבצע אימות למידע זה</li>
              <li>השירות אינו מבטיח קבלת אישור עקרוני או הלוואה בפועל.
                לחברה לא תהא כל אחריות במקרה של סירוב למתן מימון</li>
              <li>השירות מתבסס על נתוני שוק עדכניים, והמלצותיו תקפות ל-14
                יום בלבד ממועד מסירתן, בכפוף לשינויים בשיעורי הריבית</li>
              <li>סמכות השיפוט הבלעדית בכל הקשור להסכם זה נתונה לבית המשפט המוסמך בתל-אביב</li>
            </ol>
          </li>
        </ol>
      </div>

      <Signature
        value={form.values.userSignature}
        onChange={(val) => form.setFieldValue('userSignature', val)}
        required={allowSignature}
        disabled={!allowSignature}
        label="חתימה"
        error={form.errors.userSignature}
      />

    </Page>
  );
};

export default Agree;
